/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { isEqual } from 'lodash';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePreventBrowserClose } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  drawingToolActions,
  drawingToolSelectors,
  layerActions,
  layerSelectors,
  layerUtils,
  uiSelectors,
} from '@opengeoweb/store';
import { DRAWMODE, emptyGeoJSON } from '@opengeoweb/webmap-react';
import type { DrawMode } from '@opengeoweb/webmap-react';
import { getComparableDrawing } from './drawingObjectUtils';
import { DrawingToolForm } from './DrawingToolForm';
import type { DrawingToolFormValues } from './DrawingToolFormContents';
import {
  MASKED_FILL_OPACITY_KEY,
  clipGeoJSONWithMask,
  findMaskForTransformation,
  getDrawnFeatureIndexes,
  getFeatureIndexesToHideFill,
  getFeatureIndexesToRestoreFill,
  restoreMaskedFill,
  restoreMaskedFillProperties,
} from './maskUtils';
import {
  useClippedResultLayer,
  useMaskPreviewLayer,
} from './useMaskPreviewLayer';
import { useSelectDrawFeature } from './useSelectDrawFeature';
import {
  useDrawingDetails,
  useDrawings,
  useMutateSaveOrUpdateDrawing,
} from '../../api/warning-api/hooks';
import { warningsDrawingsActions } from '../../store/warningsDrawings/reducer';
import {
  getWarningFormDirty,
  selectDrawByInstanceId,
} from '../../store/warningsDrawings/selectors';
import type { DrawingListItem } from '../../store/warningsDrawings/types';
import { drawingDialogType } from '../../store/warningsDrawings/utils';
import type { WarningType } from '../../store/warningsForm/types';
import { useWarningsTranslation } from '../../utils/i18n';
import { useShareDrawing } from '../AreaDrawingManager/shareDrawing';

const emptyMasks: DrawingListItem[] = [];

export const SAVED_MASK_ID = 'saved-mask';

type MaskError =
  | 'drawing-tool-form-mask-no-overlap'
  | 'drawing-tool-form-mask-clip-error';

interface DrawingToolMasks {
  masks: DrawingListItem[];
  isLoading: boolean;
  selectedMask?: DrawingListItem;
  selectedMaskId?: string;
  maskError?: MaskError;
  onSelectMask: (maskId?: string) => void;
  onHoverMask: (maskId?: string) => void;
}

interface ClippedGeoJSON {
  clippedGeoJSON?: GeoJSON.FeatureCollection;
  hasClipError?: boolean;
}

const useRestoreSavedMask = (
  drawingId: string,
  masks: DrawingListItem[],
  isLoadingMasks: boolean,
  onRestoreMask: (maskId: string) => void,
): DrawingListItem | undefined => {
  const { t } = useWarningsTranslation();
  const { data: drawingDetails } = useDrawingDetails(drawingId);
  const restoredDrawingIdRef = React.useRef<string>(undefined);
  const [unmatchedSavedMask, setUnmatchedSavedMask] =
    React.useState<DrawingListItem>();

  React.useEffect(() => {
    if (
      !drawingId ||
      restoredDrawingIdRef.current === drawingId ||
      drawingDetails?.id !== drawingId ||
      isLoadingMasks
    ) {
      return;
    }
    restoredDrawingIdRef.current = drawingId;
    const savedMask = findMaskForTransformation(
      masks,
      drawingDetails.transformation,
    );
    if (savedMask) {
      onRestoreMask(savedMask.id);
      return;
    }
    if (drawingDetails.transformation) {
      setUnmatchedSavedMask({
        id: SAVED_MASK_ID,
        objectName: t('drawing-tool-form-mask-saved'),
        geoJSON: drawingDetails.transformation.mask,
        lastUpdatedTime: drawingDetails.lastUpdatedTime,
        scope: 'user',
      });
      onRestoreMask(SAVED_MASK_ID);
    }
  }, [drawingDetails, drawingId, isLoadingMasks, masks, onRestoreMask, t]);

  return unmatchedSavedMask;
};

const useClippedGeoJSON = (
  mapId: string,
  geoJSON?: GeoJSON.FeatureCollection,
  mask?: DrawingListItem,
): ClippedGeoJSON => {
  const result = React.useMemo((): ClippedGeoJSON => {
    if (!mask || !geoJSON) {
      return {};
    }
    try {
      return { clippedGeoJSON: clipGeoJSONWithMask(geoJSON, mask.geoJSON) };
    } catch {
      return { hasClipError: true };
    }
  }, [geoJSON, mask]);
  useClippedResultLayer(mapId, result.clippedGeoJSON);
  return result;
};

const getMaskError = (
  { clippedGeoJSON, hasClipError }: ClippedGeoJSON,
  geoJSON?: GeoJSON.FeatureCollection,
): MaskError | undefined => {
  if (hasClipError) {
    return 'drawing-tool-form-mask-clip-error';
  }
  const hasDrawnFeatures =
    !!geoJSON && getDrawnFeatureIndexes(geoJSON).length > 0;
  return clippedGeoJSON && hasDrawnFeatures && !clippedGeoJSON.features.length
    ? 'drawing-tool-form-mask-no-overlap'
    : undefined;
};

const useHideFillOutsideMask = (
  layerId: string | undefined,
  geoJSON: GeoJSON.FeatureCollection | undefined,
  isMaskSelected: boolean,
): void => {
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (!layerId || !geoJSON) {
      return;
    }
    if (isMaskSelected) {
      getFeatureIndexesToHideFill(geoJSON).forEach((targetFeatureIndex) => {
        dispatch(
          layerActions.updateFeatureProperties({
            layerId,
            properties: {
              'fill-opacity': 0,
              [MASKED_FILL_OPACITY_KEY]:
                geoJSON.features[targetFeatureIndex].properties?.[
                  'fill-opacity'
                ] ?? null,
            },
            targetFeatureIndex,
            origin: 'DrawingToolFormConnect',
          }),
        );
      });
      return;
    }
    getFeatureIndexesToRestoreFill(geoJSON).forEach((targetFeatureIndex) => {
      dispatch(
        layerActions.updateFeatureProperties({
          layerId,
          properties: {
            'fill-opacity':
              geoJSON.features[targetFeatureIndex].properties?.[
                MASKED_FILL_OPACITY_KEY
              ] ?? undefined,
            [MASKED_FILL_OPACITY_KEY]: undefined,
          },
          targetFeatureIndex,
          origin: 'DrawingToolFormConnect',
        }),
      );
    });
  }, [dispatch, geoJSON, isMaskSelected, layerId]);
};

const useDrawingToolMasks = (
  drawingId: string,
  geoJSON?: GeoJSON.FeatureCollection,
): DrawingToolMasks => {
  const { data: fetchedMasks = emptyMasks, isLoading } = useDrawings({
    keywords: 'mask',
  });
  const [selectedMaskId, setSelectedMaskId] = React.useState<string>();
  const [hoveredMaskId, setHoveredMaskId] = React.useState<string>();
  const mapId = useSelector((store: CoreAppStore) =>
    uiSelectors.getDialogMapId(store, drawingDialogType),
  );

  const unmatchedSavedMask = useRestoreSavedMask(
    drawingId,
    fetchedMasks,
    isLoading,
    setSelectedMaskId,
  );
  const masks = React.useMemo(
    () =>
      unmatchedSavedMask ? [...fetchedMasks, unmatchedSavedMask] : fetchedMasks,
    [fetchedMasks, unmatchedSavedMask],
  );

  const selectedMask = masks.find((mask) => mask.id === selectedMaskId);
  const previewMask =
    masks.find((mask) => mask.id === hoveredMaskId) ?? selectedMask;
  useMaskPreviewLayer(mapId, previewMask);

  const clipResult = useClippedGeoJSON(mapId, geoJSON, selectedMask);

  const onSelectMask = React.useCallback((maskId?: string): void => {
    setSelectedMaskId(maskId);
  }, []);

  return {
    masks,
    isLoading,
    selectedMask,
    selectedMaskId,
    maskError: getMaskError(clipResult, geoJSON),
    onSelectMask,
    onHoverMask: setHoveredMaskId,
  };
};

export interface DrawingToolFormConnectProps {
  /** called after the drawing has been shared with a warning form */
  onShared?: () => void;
}

export const DrawingToolFormConnect: React.FC<DrawingToolFormConnectProps> = ({
  onShared = (): void => {},
}) => {
  const dispatch = useDispatch();
  const {
    mutate: saveOrUpdateDrawing,
    isPending,
    error,
  } = useMutateSaveOrUpdateDrawing();
  const drawing = useSelector((store: CoreAppStore) =>
    selectDrawByInstanceId(store, drawingDialogType),
  );
  const {
    data: savedDrawing,
    isError: hasSavedDrawingError,
    isLoading: isLoadingSavedDrawing,
    refetch: refetchSavedDrawing,
  } = useDrawingDetails(drawing?.id ?? '');
  const drawToolId = drawingDialogType;
  const drawTool = useSelector((store: CoreAppStore) =>
    drawingToolSelectors.selectDrawToolById(store, drawToolId),
  );

  const layerFeatureGeoJSON = useSelector((store: CoreAppStore) =>
    layerSelectors.getFeatureLayerGeoJSON(store, drawTool?.geoJSONLayerId),
  );

  const isLayerInEditMode = useSelector((store: CoreAppStore) =>
    layerSelectors.getIsLayerInEditMode(store, drawTool?.geoJSONLayerId),
  );

  const featureLayerGeoJSONProperties = useSelector((store: CoreAppStore) =>
    layerSelectors.getFeatureLayerGeoJSONProperties(
      store,
      drawTool?.geoJSONLayerId,
    ),
  );

  const onSelectFeature = useSelectDrawFeature(drawTool?.geoJSONLayerId);

  const isFormDirty = useSelector((store: CoreAppStore) =>
    getWarningFormDirty(store, drawingDialogType),
  );

  usePreventBrowserClose(isFormDirty || isLayerInEditMode);

  const {
    masks,
    isLoading: isLoadingMasks,
    selectedMask,
    selectedMaskId,
    maskError,
    onSelectMask,
    onHoverMask,
  } = useDrawingToolMasks(drawing?.id ?? '', layerFeatureGeoJSON);

  useHideFillOutsideMask(
    drawTool?.geoJSONLayerId,
    layerFeatureGeoJSON,
    !!selectedMask,
  );

  const formGeoJSONRef = React.useRef<GeoJSON.FeatureCollection>(undefined);
  const formGeoJSON = React.useMemo(() => {
    const restoredGeoJSON =
      layerFeatureGeoJSON && restoreMaskedFill(layerFeatureGeoJSON);
    if (!isEqual(formGeoJSONRef.current, restoredGeoJSON)) {
      formGeoJSONRef.current = restoredGeoJSON;
    }
    return formGeoJSONRef.current;
  }, [layerFeatureGeoJSON]);

  const geoJSONProperties = React.useMemo(
    () => restoreMaskedFillProperties(featureLayerGeoJSONProperties),
    [featureLayerGeoJSONProperties],
  );

  const onSubmitForm = React.useCallback(
    (formValues: DrawingToolFormValues) => {
      // the stored drawing could not be fetched, so unsaved changes cannot be
      // detected: retry fetching it, saving is the moment it matters again
      if (hasSavedDrawingError) {
        void refetchSavedDrawing();
      }
      const original = formValues.geoJSON;
      saveOrUpdateDrawing({
        id: drawing?.id,
        data: {
          geoJSON: selectedMask
            ? clipGeoJSONWithMask(original, selectedMask.geoJSON)
            : original,
          objectName: formValues.objectName,
          scope: 'user',
          transformation: selectedMask
            ? { mask: selectedMask.geoJSON, original }
            : null,
        },
      });
    },
    [
      drawing?.id,
      hasSavedDrawingError,
      refetchSavedDrawing,
      saveOrUpdateDrawing,
      selectedMask,
    ],
  );

  const onChangeGeoJSONProperties = React.useCallback(
    (properties: GeoJSON.GeoJsonProperties): void => {
      const layerId = drawTool?.geoJSONLayerId!;
      dispatch(
        layerActions.updateFeatureProperties({
          layerId,
          properties,
          origin: 'DrawingToolFormConnect',
        }),
      );
      const fillOpacity = properties?.['fill-opacity'];
      if (!selectedMask || typeof fillOpacity !== 'number') {
        return;
      }
      getDrawnFeatureIndexes(layerFeatureGeoJSON ?? emptyGeoJSON).forEach(
        (targetFeatureIndex) => {
          dispatch(
            layerActions.updateFeatureProperties({
              layerId,
              properties: {
                'fill-opacity': 0,
                [MASKED_FILL_OPACITY_KEY]: fillOpacity,
              },
              targetFeatureIndex,
              origin: 'DrawingToolFormConnect',
            }),
          );
        },
      );
    },
    [dispatch, drawTool?.geoJSONLayerId, layerFeatureGeoJSON, selectedMask],
  );

  const onChangeObjectName = React.useCallback(
    (featureId: string, objectName: string): void => {
      const featureIndex = (layerFeatureGeoJSON?.features ?? []).findIndex(
        (feature) => layerUtils.getFeatureSelectionKey(feature) === featureId,
      );

      if (featureIndex < 0) {
        return;
      }

      dispatch(
        layerActions.updateFeatureProperties({
          layerId: drawTool?.geoJSONLayerId!,
          properties: { objectName },
          targetFeatureIndex: featureIndex,
          origin: 'DrawingToolFormConnect',
        }),
      );
    },
    [dispatch, drawTool?.geoJSONLayerId, layerFeatureGeoJSON?.features],
  );

  const onDeleteFeature = React.useCallback(
    (featureId: string): void => {
      dispatch(
        layerActions.deleteFeature({
          layerId: drawTool?.geoJSONLayerId!,
          featureId,
        }),
      );
    },
    [dispatch, drawTool?.geoJSONLayerId],
  );

  const onChangeDrawMode = React.useCallback(
    (tool: DrawMode): void => {
      const shouldUpdateShape = tool.value !== DRAWMODE.SPLIT;
      dispatch(
        drawingToolActions.changeDrawToolMode({
          drawToolId,
          drawModeId: tool.drawModeId,
          shouldUpdateShape,
        }),
      );
    },
    [dispatch, drawToolId],
  );

  const onDeactivateTool = React.useCallback((): void => {
    dispatch(
      layerActions.toggleFeatureMode({
        layerId: drawTool?.geoJSONLayerId!,
        isInEditMode: false,
        drawMode: '',
      }),
    );
    if (drawTool?.activeDrawModeId) {
      dispatch(
        drawingToolActions.changeDrawToolMode({
          drawToolId,
          drawModeId: drawTool?.activeDrawModeId,
        }),
      );
    }
  }, [
    dispatch,
    drawTool?.activeDrawModeId,
    drawTool?.geoJSONLayerId,
    drawToolId,
  ]);

  const onChangeName = React.useCallback(
    (newName: string): void => {
      dispatch(
        warningsDrawingsActions.setDrawValues({
          objectName: newName,
          id: drawing?.id || '',
          drawingInstanceId: drawingDialogType,
          isFormDirty: drawing?.isFormDirty,
        }),
      );
    },
    [dispatch, drawing?.id, drawing?.isFormDirty],
  );

  const onChangeFormDirty = React.useCallback(
    (isFormDirty: boolean): void => {
      dispatch(
        warningsDrawingsActions.setFormDirty({
          drawingInstanceId: drawingDialogType,
          isFormDirty,
        }),
      );
    },
    [dispatch],
  );

  const shareDrawing = useShareDrawing();
  const onShareDrawing = React.useCallback(
    (type: WarningType): void => {
      // Can only share saved drawings
      if (!savedDrawing) {
        return;
      }
      onShared();
      shareDrawing(savedDrawing, type);
    },
    [onShared, savedDrawing, shareDrawing],
  );

  const comparableSavedDrawing = React.useMemo(
    () => (savedDrawing ? getComparableDrawing(savedDrawing) : undefined),
    [savedDrawing],
  );

  return (
    <DrawingToolForm
      activeDrawModeId={drawTool?.activeDrawModeId || ''}
      dialogError={error?.message}
      drawModes={drawTool?.drawModes!}
      drawingName={drawing?.objectName}
      geoJSON={formGeoJSON}
      geoJSONProperties={geoJSONProperties}
      id={drawing?.id}
      isInEditMode={isLayerInEditMode}
      isLoading={isPending}
      isLoadingMasks={isLoadingMasks}
      isLoadingSavedDrawing={isLoadingSavedDrawing}
      maskError={maskError}
      masks={masks}
      onChangeDrawMode={onChangeDrawMode}
      onChangeName={onChangeName}
      onChangeObjectName={onChangeObjectName}
      onChangeProperties={onChangeGeoJSONProperties}
      onClickShare={onShareDrawing}
      onDeactivateTool={onDeactivateTool}
      onDeleteFeature={onDeleteFeature}
      onFormDirty={onChangeFormDirty}
      onHoverMask={onHoverMask}
      onSelectFeature={onSelectFeature}
      onSelectMask={onSelectMask}
      onSubmitForm={onSubmitForm}
      savedDrawing={comparableSavedDrawing}
      selectedMask={selectedMask?.geoJSON}
      selectedMaskId={selectedMaskId}
    />
  );
};
